import React from 'react';
import PropTypes from 'prop-types';
import css from './StrapiContentRenderer.css';

// Slugify heading text to match articleEnhancements.slugifyHeading exactly, so
// the on-page Table of Contents jump-links resolve to these heading ids.
const slugifyHeadingText = text => {
  const s = (text == null ? '' : String(text))
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '');
  return s || null;
};

// Extract plain text from a block node subtree (for heading id generation).
const nodeText = node => {
  if (!node) return '';
  if (typeof node === 'string') return node;
  if (node.type === 'text') return node.text || '';
  if (Array.isArray(node.children)) return node.children.map(nodeText).join('');
  return '';
};

// Block Components
const Paragraph = ({ children }) => <p>{children}</p>;
const Heading = ({ level, children, id }) => {
  const Tag = `h${level}`;
  return <Tag id={id}>{children}</Tag>;
};

const List = ({ format, children }) => {
  const Tag = format === 'ordered' ? 'ol' : 'ul';
  return <Tag>{children}</Tag>;
};

const Quote = ({ children }) => <blockquote>{children}</blockquote>;
const CodeBlock = ({ plainText }) => (
  <pre>
    <code>{plainText}</code>
  </pre>
);

const Image = ({ src, alt }) => <img src={src} alt={alt} />;

// Affiliate / paid outbound links (Amazon Associates, RideDog, Tractor Supply /
// Pepperjam, CJ, etc.) must carry rel="sponsored nofollow" per Google's
// link-qualification guidelines so the site is never mis-read as a thin
// affiliate site. Internal + normal editorial links are left untouched (adding
// nofollow to internal links would harm SEO).
//
// Allow-list of affiliate signals. Add a pattern here whenever a new affiliate
// network/retailer is wired into blog bodies. Keep it specific (match the
// affiliate param/host, not every link to the retailer's bare domain) so
// genuine editorial links are not accidentally nofollowed.
const AFFILIATE_URL_PATTERNS = [
  // Amazon Associates
  /(^|\/\/|\.)amazon\.[a-z.]+/i,
  /amzn\.to\//i,
  /[?&]tag=bigloads-/i,
  // RideDog (Rewardful ?via= deep links, e.g. ?via=Big-Loads)
  /(^|\/\/|\.)ridedog\./i,
  /[?&]via=/i,
  // Tractor Supply via Pepperjam / Ascend
  /(^|\/\/|\.)tractorsupply\.com/i,
  /(^|\/\/|\.)pepperjam(network)?\.com/i,
  /(^|\/\/|\.)gopjn\.com/i,
  // Commission Junction (CJ)
  /(^|\/\/|\.)(cj|anrdoezrs|dpbolvw|jdoqocy|kqzyfj|tkqlhce)\.(com|net)/i,
  /(^|\/\/|\.)commission-junction\.com/i,
];
const isAffiliateUrl = url => {
  if (typeof url !== 'string') return false;
  return AFFILIATE_URL_PATTERNS.some(re => re.test(url));
};

// Shared rel attribute for outbound links so the block-node path and the
// markdown path qualify affiliate links identically. Affiliate links get
// sponsored+nofollow; other external links keep the safe default.
const relForUrl = url =>
  isAffiliateUrl(url) ? 'sponsored nofollow noopener noreferrer' : 'noopener noreferrer';

const Link = ({ url, children }) => {
  if (isAffiliateUrl(url)) {
    return (
      <a href={url} rel={relForUrl(url)} target="_blank">
        {children}
      </a>
    );
  }
  return <a href={url}>{children}</a>;
};

// Inline Modifier Components
const Bold = ({ children }) => <strong>{children}</strong>;
const Italic = ({ children }) => <em>{children}</em>;
const Underline = ({ children }) => <u>{children}</u>;
const Strikethrough = ({ children }) => <s>{children}</s>;
const InlineCode = ({ children }) => <code>{children}</code>;

// Render Inline Modifiers
const renderInlineModifiers = fragments => {
  if (!Array.isArray(fragments)) return fragments;

  return fragments.map((fragment, index) => {
    if (typeof fragment === 'string') return fragment;

    // Handle text fragment with multiple styles
    if (fragment.type === 'text') {
      let content = fragment.text;

      // Wrap content in style components based on properties
      if (fragment.bold) {
        content = <Bold key={`bold-${index}`}>{content}</Bold>;
      }
      if (fragment.italic) {
        content = <Italic key={`italic-${index}`}>{content}</Italic>;
      }
      if (fragment.underline) {
        content = <Underline key={`underline-${index}`}>{content}</Underline>;
      }

      return content;
    }

    // Handle nested modifiers (e.g., 'bold' wrapping 'italic')
    switch (fragment.type) {
      case 'bold':
        return <Bold key={index}>{renderInlineModifiers(fragment.children)}</Bold>;
      case 'italic':
        return <Italic key={index}>{renderInlineModifiers(fragment.children)}</Italic>;
      case 'underline':
        return <Underline key={index}>{renderInlineModifiers(fragment.children)}</Underline>;
      case 'strikethrough':
        return (
          <Strikethrough key={index}>{renderInlineModifiers(fragment.children)}</Strikethrough>
        );
      case 'code':
        return <InlineCode key={index}>{renderInlineModifiers(fragment.children)}</InlineCode>;
      default:
        return fragment.text; // Render plain text
    }
  });
};

// Render JSON Nodes. `ctx` carries a per-render de-dupe map so heading ids
// match the TOC helper's -2/-3 suffixing exactly.
const renderNode = (node, ctx) => {
  if (!node) return null;

  switch (node.type) {
    case 'paragraph':
      return (
        <Paragraph key={node.id}>
          {node.children && node.children.map(child => renderNode(child, ctx))}
        </Paragraph>
      );
    case 'heading': {
      let headingId;
      if ((node.level === 2 || node.level === 3) && ctx && ctx.seen) {
        let base = slugifyHeadingText(nodeText(node));
        if (base) {
          if (ctx.seen[base] == null) ctx.seen[base] = 1;
          else {
            ctx.seen[base] += 1;
            base = `${base}-${ctx.seen[base]}`;
          }
          headingId = base;
        }
      }
      return (
        <Heading key={node.id} level={node.level} id={headingId}>
          {node.children && node.children.map(child => renderNode(child, ctx))}
        </Heading>
      );
    }
    case 'list':
      return (
        <List key={node.id} format={node.format}>
          {node.children && node.children.map(child => renderNode(child, ctx))}
        </List>
      );
    case 'list-item':
      return (
        <li key={node.id}>{node.children && node.children.map(child => renderNode(child, ctx))}</li>
      );
    case 'quote':
      return (
        <Quote key={node.id}>
          {node.children && node.children.map(child => renderNode(child, ctx))}
        </Quote>
      );
    case 'code':
      return <CodeBlock key={node.id} plainText={node.plainText} />;
    case 'image':
      return <Image key={node.id} src={node.image.url} alt={node.image.alt || 'Image'} />;
    case 'link':
      return (
        <Link key={node.id} url={node.url}>
          {node.children && node.children.map(child => renderNode(child, ctx))}
        </Link>
      );
    case 'text':
      return renderInlineModifiers([node]); // Process single text node with modifiers
    default:
      return null;
  }
};

// Parse Markdown Content
const parseMarkdown = text => {
  const inlineStyles = [
    { regex: /\*\*(.*?)\*\*/g, tag: content => <Bold>{content}</Bold> },
    { regex: /_(.*?)_/g, tag: content => <Italic>{content}</Italic> },
    { regex: /`(.*?)`/g, tag: content => <InlineCode>{content}</InlineCode> },
    { regex: /~~(.*?)~~/g, tag: content => <Strikethrough>{content}</Strikethrough> },
  ];

  return text.split('\n').map((line, index) => {
    // Handle headings
    const headingMatch = line.match(/^(#+)\s+(.*)$/);
    if (headingMatch) {
      const level = headingMatch[1].length; // Number of `#` determines heading level
      const content = headingMatch[2];
      return React.createElement(`h${level}`, { key: index }, content);
    }

    // Handle links. Use a fresh regex per line (the /g lastIndex is otherwise
    // shared across iterations) and split on the RAW array WITHOUT filtering,
    // so the [before, text, url, after, ...] alignment is stable even when a
    // line starts with a link (a leading empty string must keep its slot or the
    // i%3 indexing shifts and href ends up pointing at the anchor text).
    const linkRegex = /\[([^\]]+)\]\((https?:\/\/[^\)]+)\)/g;
    if (linkRegex.test(line)) {
      const parts = line.split(/\[([^\]]+)\]\((https?:\/\/[^\)]+)\)/g);
      // parts = [text0, label1, url1, text1, label2, url2, text2, ...]
      // index % 3: 0 = plain text, 1 = link label, 2 = link url.
      const nodes = [];
      for (let i = 0; i < parts.length; i += 1) {
        const slot = i % 3;
        if (slot === 0) {
          if (parts[i]) nodes.push(parts[i]);
        } else if (slot === 2) {
          const url = parts[i];
          const label = parts[i - 1];
          nodes.push(
            <a key={i} href={url} target="_blank" rel={relForUrl(url)}>
              {label}
            </a>
          );
        }
        // slot === 1 (label) is consumed when we reach its url at slot === 2.
      }
      return <p key={index}>{nodes}</p>;
    }

    // Apply inline styles
    let formattedLine = line;
    inlineStyles.forEach(({ regex, tag }) => {
      formattedLine = formattedLine.replace(regex, (_, match) => {
        return React.createElement(tag, { key: match }, match);
      });
    });

    // Handle bullet lists
    if (line.startsWith('- ') || line.startsWith('* ')) {
      return <li key={index}>{formattedLine.slice(2)}</li>;
    }

    // Default to rendering as a paragraph
    return <Paragraph key={index}>{formattedLine}</Paragraph>;
  });
};

// Main Component
const StrapiContentRenderer = ({ content, className }) => {
  if (!content) {
    return null;
  }

  // Per-render de-dupe context so heading ids line up with the TOC helper.
  const ctx = { seen: Object.create(null) };

  return (
    <div className={className || css.contentRenderer}>
      {content.map(block => {
        if (typeof block === 'string') {
          return parseMarkdown(block).map(node => node);
        }
        // Render JSON nodes
        return renderNode(block, ctx);
      })}
    </div>
  );
};

StrapiContentRenderer.propTypes = {
  content: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.object, PropTypes.string])).isRequired,
  className: PropTypes.string,
};

export default StrapiContentRenderer;
